import shaka from 'shaka-player';
import { findShowEpisodeByPlayableId } from '../../utils/viewable';

export function extractContentIdFromInitData(initData) {
  const prefix = 'skd://';
  const initDataStr = shaka.util.StringUtils.fromBytesAutoDetect(initData);

  const pos = initDataStr.indexOf(prefix);
  if (pos === -1) {
    return '';
  }

  return initDataStr.substring(pos + prefix.length);
}

export function sortTracksByBandwidthAsc(trackA, trackB) {
  return trackA.bandwidth - trackB.bandwidth;
}

export function getNormalizedBuffer(playerElement) {
  const {
    buffered,
    currentTime,
  } = playerElement;

  if (!buffered.length) {
    return {
      start: 0,
      end: 0,
    };
  }

  if (buffered.length === 1) {
    return {
      start: Math.round(buffered.start(0)),
      end: Math.round(buffered.end(0)),
    };
  }

  for (let i = 0; i < buffered.length; i += 1) {
    if (buffered.start(i) <= currentTime && buffered.end(i) > currentTime) {
      return {
        start: Math.round(buffered.start(i)),
        end: Math.round(buffered.end(i)),
      };
    }
  }

  return {
    start: 0,
    end: 0,
  };
}

/**
 * Helper method to filter the same lang tracks
 * @param tracks
 * @returns {Array}
 */
export function filterForUniqueLanguages(tracks) {
  const languageMap = new Map();

  tracks.forEach((track) => {
    if (
      languageMap.has(track.language)
      && !track.label?.endsWith('hoh') // high priority track
    ) {
      const existingTrack = languageMap.get(track.language);
      if (
        track.mimeType === 'application/mp4'
        && existingTrack.mimeType !== 'application/mp4'
        && !existingTrack.label?.endsWith('hoh')
      ) {
        languageMap.set(track.language, track);
      }
    } else {
      languageMap.set(track.language, track);
    }
  });

  return Array.from(languageMap.values());
}

/**
 * Helper method to normalize tracks
 * @param tracks
 * @returns {Array}
 */
export function normalizeTracks(tracks) {
  return tracks
    .filter(track => track.kind !== 'metadata')
    .map((track, i) => ({
      ...track,
      active: track.enabled || track.mode === 'showing',
      language: track.language,
      id: track.id || i,
      bandwidth: -1,
      label: track.label || '',
      kind: track.kind || '',
      mode: 'disabled',
    }));
}

export function pickTrackByQuality(variantTracks, quality) {
  const mediumQualityVideo = variantTracks[Math.floor(variantTracks.length / 2)];
  const highQualityVideo = variantTracks[variantTracks.length - 1];
  const lowQualityVideo = variantTracks[0];

  if (quality === 'low') {
    return lowQualityVideo;
  }

  if (quality === 'medium') {
    return mediumQualityVideo;
  }

  if (quality === 'high') {
    return highQualityVideo;
  }

  throw new Error(`unexpected quality level: ${quality}`);
}

export const b64EncodeUnicode = str => btoa(encodeURIComponent(str)
  .replace(/%([0-9A-F]{2})/g,
    (match, p1) => String.fromCharCode(`0x${p1}`)));

export const parseHlsAttributes = (tag) => {
  const blocks = tag.match(/^#(EXT[^:]*)(?::(.*))?$/);
  const data = blocks[2];
  const attributes = {};

  if (data) {
    const attributeRegex = /([^=]+)=(?:"([^"]*)"|([^",]*))(?:,|$)/g;

    // iterate over all found matches
    data.replace(attributeRegex, (_, attrName, attrValue1, attrValue2) => {
      attributes[attrName] = attrValue1 ?? attrValue2;
    });
  }

  return attributes;
};

function getShowData(viewable, assetId) {
  const type = viewable.__typename?.toLowerCase();

  if (type === 'show') {
    const episode = findShowEpisodeByPlayableId(viewable, assetId);

    return {
      video_content_type: 'episode',
      video_title: episode.title,
    };
  }
  return {};
}

function getChannelData(viewable) {
  const type = viewable.__typename?.toLowerCase();

  if (type === 'channel' && viewable?.broadcastById) {
    return {
      video_title: viewable.broadcastById.title,
    };
  }
  return {};
}

export function getStreamType(viewable) {
  const playable = viewable?.defaultPlayable || viewable?.playable;

  switch (playable?.kind?.toLowerCase()) {
    case 'live_event': {
      const now = Math.round((new Date()).getTime() / 1000);
      const endTime = viewable?.defaultPlayable?.catchupStop || now;

      if (endTime >= now) {
        return 'live';
      }
      return 'catchup';
    }

    case 'channel':
      if (viewable?.broadcastById) {
        return 'catchup';
      }
      return 'live';

    case 'broadcast':
      return 'catchup';

    default:
      return 'vod';
  }
}

export const getMuxMetadata = (model, viewable) => ({
  video_id: model.assetId,
  video_title: viewable.title,
  video_content_type: viewable.__typename?.toLowerCase(),
  video_cdn: 'cloudfront',
  video_stream_type: getStreamType(viewable),
  sub_property_id: model.partner,
  custom_1: viewable.genres?.[0]?.trim(),
  custom_2: model.drm,
  view_client_application_name: model.clientApplicationName,
  view_client_application_version: model.clientApplicationVersion,
  ...getShowData(viewable, model.assetId),
  ...getChannelData(viewable),
});
